// @ts-nocheck
import Parchment from 'parchment'
import DOMPurify from 'dompurify'
import { textEditorSanitizeConfig } from '@/constants/validate'
import Delta from 'quill-delta'
import { Quill } from '@vueup/vue-quill'
import ErrorToast from '@/components/toast-error/indexErrorToast.vue'
import Toast, { useToast } from 'vue-toastification'

const toast = useToast()

let alignConfig = {
  scope: Parchment.Scope.BLOCK,
  whitelist: ['right', 'center', 'justify'],
}
let AlignAttribute = new Parchment.Attributor.Attribute('align', 'align', alignConfig)
let AlignStyle = new Parchment.Attributor.Style('align', 'text-align', alignConfig)

let directionConfig = {
  scope: Parchment.Scope.BLOCK,
  whitelist: ['rtl'],
}

let DirectionAttribute = new Parchment.Attributor.Attribute('direction', 'dir', directionConfig)
let DirectionStyle = new Parchment.Attributor.Style('direction', 'direction', directionConfig)

let SizeStyle = new Parchment.Attributor.Style('size', 'font-size', {
  scope: Parchment.Scope.INLINE,
  whitelist: ['10px', '18px', '32px'],
})

let FontStyle = new Parchment.Attributor.Style('font', 'font-family', {
  scope: Parchment.Scope.INLINE,
  whitelist: ['zenkaku', 'mplus', 'notoserif'],
})

const MAPPING_STYLE_NAME = {
  background: 'background-color',
}

class ColorAttributor extends Parchment.Attributor.Style {
  value(domNode) {
    let value = super.value(domNode)
    if (!value.startsWith('rgb(')) return value
    value = value.replace(/^[^\d]+/, '').replace(/[^\d]+$/, '')
    return (
      '#' +
      value
        .split(',')
        .map(function (component) {
          return ('00' + parseInt(component).toString(16)).slice(-2)
        })
        .join('')
    )
  }
}

let ColorStyle = new ColorAttributor('color', 'color', {
  scope: Parchment.Scope.INLINE,
})

let BackgroundStyle = new ColorAttributor('background', 'background-color', {
  scope: Parchment.Scope.INLINE,
})

const Clipboard = Quill.import('modules/clipboard')

const ATTRIBUTE_ATTRIBUTORS = [AlignAttribute, DirectionAttribute].reduce(function (memo, attr) {
  memo[attr.keyName] = attr
  return memo
}, {})

const STYLE_ATTRIBUTORS = [AlignStyle, BackgroundStyle, ColorStyle, DirectionStyle, SizeStyle, FontStyle].reduce(function (memo, attr) {
  memo[attr.keyName] = attr
  return memo
}, {})

declare global {
  interface Window {
    clipboardData: any
  }
}

let isGoogleDocsPaste = false

class CustomClipboard extends Clipboard {
  onPaste(e: ClipboardEvent) {
    e.preventDefault()

    const clipboardData = e.clipboardData || window.clipboardData
    const html = clipboardData.getData('text/html')
    const text = clipboardData.getData('text/plain')

    if (!html) {
      // @ts-ignore
      this.quill.insertText(this.quill.getSelection().index, text)
      return
    }

    // Detect snapshot image from Word/Libre clipboard (image/* as file)
    // and stop Quill default image paste to avoid duplicated snapshot
    const items = Array.from(clipboardData.items || [])
    const snapshotImage = items.find((item) => item.kind === 'file' && item.type.startsWith('image/'))
    if (snapshotImage) {
      // Stop other paste handlers (Quill default image paste)
      // so the snapshot image will NOT be inserted
      e.stopImmediatePropagation()
    }

    isGoogleDocsPaste = html.includes('docs-internal-guid')

    // Remove 2 style not need, backgound white and color black
    // It make icon background color on toolbar active when copy default text
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html

    // Remove fake <b> wrappers from Google Docs
    unwrapGoogleDocsBold(tempDiv)
    if (imageInvalidDomain(tempDiv)) {
      e.stopImmediatePropagation()
      toast.error({
        component: ErrorToast,
        props: {
          messageCode: 'VALIDATE_EXTERNAL_IMAGE_PASTE_BLOCKED',
        },
      })
      return
    }

    tempDiv.querySelectorAll('*').forEach((el) => {
      if (el.style) {
        if (el.style.backgroundColor === 'rgb(255, 255, 255)') {
          el.style.backgroundColor = ''
        }
        if (el.style.color === 'rgb(33, 37, 41)') {
          el.style.color = ''
        }
      }
    })

    const contentDelta = this.convert(tempDiv.innerHTML)

    let range = this.quill.getSelection()
    let delta = new Delta().retain(range.index)
    this.quill.selection.update(Quill.sources.SILENT)
    setTimeout(() => {
      delta = delta.delete(range.length).concat(contentDelta)
      this.quill.updateContents(delta, Quill.sources.USER)
      this.quill.setSelection(delta.length() - range.length, Quill.sources.SILENT)
      this.quill.focus()
    }, 0)
  }

  prepareMatching() {
    let elementMatchers = [],
      textMatchers = []
    this.matchers.forEach((pair) => {
      let [selector, matcher] = pair
      switch (selector) {
        case Node.TEXT_NODE:
          textMatchers.push(matcher)
          break
        case Node.ELEMENT_NODE:
          elementMatchers.push(matcher)
          break
        default:
          ;[].forEach.call(this.container.querySelectorAll(selector), (node) => {
            node['__ql-matcher'] = node['__ql-matcher'] || []
            node['__ql-matcher'].push(matcher)
          })
          break
      }
    })
    elementMatchers[3] = matchAttributor
    return [elementMatchers, textMatchers]
  }
}

const matchAttributor = (node, delta) => {
  let attributes = Parchment.Attributor.Attribute.keys(node)
  let classes = Parchment.Attributor.Class.keys(node)
  let styles = Parchment.Attributor.Style.keys(node)
  let formats = {}
  attributes
    .concat(classes)
    .concat(styles)
    .forEach((name) => {
      let attr = Parchment.query(name, Parchment.Scope.ATTRIBUTE)
      if (attr != null) {
        formats[attr.attrName] = attr.value(node)
        if (formats[attr.attrName]) return
      }
      attr = ATTRIBUTE_ATTRIBUTORS[name]
      if (attr != null && (attr.attrName === name || attr.keyName === name)) {
        formats[attr.attrName] = attr.value(node) || undefined
      }

      // some style is special case so need mapping name (ex: background need get to background-color)
      // background is shorthand, value wil return include (color, position, repeat...)
      // Quill is not a CSS engine; it only works with simple formatting.
      const styleName = MAPPING_STYLE_NAME[name] ?? name
      attr = STYLE_ATTRIBUTORS[styleName]

      if (attr != null && (attr.attrName === styleName || attr.keyName === styleName)) {
        const style = window.getComputedStyle(node, null).getPropertyValue(attr.keyName)

        // --- FIX LINK COLOR BLACK COPY FROM WORD ---
        if (attr.attrName === 'color' && (style === 'rgb(0, 0, 0)' || style === 'black')) {
          return
        }

        if (attr.attrName === 'background') {
          // Skip default / transparent background colors added by rich text editors (Docs, Word, etc.)
          if (!style || style === 'transparent' || style === 'rgba(0, 0, 0, 0)' || style === 'rgb(255, 255, 255)') {
            return
          }
        }

        formats[attr.attrName] = style
      }
    })

  if (isGoogleDocsPaste) {
    const computedStyle = window.getComputedStyle(node)
    // --- FIX UNDERLINE COPY FROM GOOGLE DOCS ---
    if (computedStyle.textDecorationLine?.includes('underline')) {
      formats.underline = true
    }
    // Google Docs pastes default inline styles for all text.
    // Keep only semantic formats to avoid overriding editor state.
    const ALLOW = ['bold', 'italic', 'underline', 'color', 'background', 'link']
    Object.keys(formats).forEach((k) => {
      if (!ALLOW.includes(k)) delete formats[k]
    })
  }

  if (Object.keys(formats).length > 0) {
    delta = applyFormat(delta, formats)
  }
  return delta
}

function applyFormat(delta, format, value) {
  // If format is an object, apply each format key recursively
  if (typeof format === 'object') {
    return Object.keys(format).reduce((d, key) => {
      return applyFormat(d, key, format[key])
    }, delta)
  }

  const Delta = Quill.import('delta')
  const newDelta = new Delta()

  delta.ops.forEach((op) => {
    // embed (image, v.v...)
    if (typeof op.insert !== 'string') {
      // Only allow assigning the format 'link' to embed (image)
      if (format === 'link') {
        const attrs = Object.assign({}, op.attributes)
        value ? (attrs.link = value) : delete attrs.link
        newDelta.insert(op.insert, attrs)
      } else {
        // Other formatting (color, background, ...) are ignored for embedding.
        newDelta.insert(op.insert, op.attributes)
      }
      return
    }

    // Split text by newline to avoid applying attributes to '\n'
    const parts = op.insert.split('\n')

    parts.forEach((text, index) => {
      if (text.length > 0) {
        // Clone attributes to avoid mutating original delta
        const attrs = Object.assign({}, op.attributes)

        // Apply or override the target format
        if (!attrs || attrs[format] !== value) {
          attrs[format] = value
        }

        newDelta.insert(text, attrs)
      }

      // Re-insert newline without attributes
      if (index < parts.length - 1) {
        newDelta.insert('\n')
      }
    })
  })

  return newDelta
}

let slicedToArray = (function () {
  function sliceIterator(arr, i) {
    let _arr = []
    let _n = true
    let _d = false
    let _e = undefined
    try {
      for (let _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value)
        if (i && _arr.length === i) break
      }
    } catch (err) {
      _d = true
      _e = err
    } finally {
      try {
        if (!_n && _i['return']) _i['return']()
      } finally {
        if (_d) throw _e
      }
    }
    return _arr
  }
  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i)
    } else {
      throw new TypeError('Invalid attempt to destructure non-iterable instance')
    }
  }
})()

function unwrapGoogleDocsBold(root) {
  root.querySelectorAll('b[id^="docs-internal-guid"]').forEach((b) => {
    const parent = b.parentNode
    while (b.firstChild) {
      parent.insertBefore(b.firstChild, b)
    }
    parent.removeChild(b)
  })
}
const imageInvalidDomain = (dom) => {
  const imgs = Array.from(dom.querySelectorAll('img'))

  const hasBlockedImage = imgs.some((img) => {
    const src = img.getAttribute('src')
    if (src.startsWith('http://') || src.startsWith('https://')) {
      const srcUrl = new URL(src)
      return srcUrl.origin !== process.env.VUE_APP_ASSETS_DOMAIN
    }
    return false
  })

  return hasBlockedImage
}

const InlineImage = Quill.import('formats/image')

/**
 * CustomImageBlot overrides Quill's default Image format to track the image rendering lifecycle.
 * Purpose: Ensures the global loading spinner remains active until the image is 100% loaded and rendered
 * in the DOM by listening to the browser's native `load` and `error` events, eliminating UI flicker.
 */
class CustomImageBlot extends InlineImage {
  static create(value: string) {
    const node = super.create(value) as HTMLImageElement

    const handleLoad = () => {
      window.dispatchEvent(new CustomEvent('quill-image-rendered', { detail: { src: node.src } }))
    }

    // Skip temporary images (Base64 / Blob / uploading-temp ID)
    // Only track when the <img> tag has a real HTTP/HTTPS URL
    const isTempImage = node.src.startsWith('data:') || node.src.startsWith('blob:') || node.src.includes('uploading-')

    if (!isTempImage) {
      if (node.complete) {
        setTimeout(handleLoad, 0)
      } else {
        node.addEventListener('load', handleLoad, { once: true })
        node.addEventListener('error', handleLoad, { once: true })
      }
    }

    return node
  }
}

export { CustomImageBlot, CustomClipboard, slicedToArray }
